// What every screen of one machine needs, derived from the mirror (AD-34: React only renders): values with the pending
// stepper value over the provisional overlay, rights from catalog and role, the equipment, the machine's local day, and
// buttons as descriptors (label, enabled, the command, the safety question). The tab components of the design system
// render these descriptors; no decision is made in a component.
import { findCommand, orioneCommandCatalog, type CommandCatalog } from '@brewcore/protocol';
import { t, textKeys, type Lang, type TextKey } from '../i18n.js';
import { controlsUsable } from '../calm.js';
import { leadMinutes, localDayOf, zoneOf } from '../schedule.js';
import type { CommandInput } from '../commands.js';
import type { MachineView, ParamValue } from '../store.js';

export type Values = (id: string) => ParamValue | undefined;
export type TextVars = Record<string, string | number>;

export interface ScreenContext {
  view: MachineView;
  lang: Lang;
  now: number;
  value: Values;
  num(id: string, fallback?: number): number;
  /**
   * controls look usable: data there, machine online, no emergency stop (FR-14, FR-29); short gaps of the machine or
   * of this device do not count (core/calm.ts). Whether a command may go out now decides the command sender.
   */
  controllable: boolean;
  isOwner: boolean;
  /** equipment the machine reports (profile.sensors, FR-9) */
  sensors: Readonly<Record<string, boolean>>;
  /** the scale is switched on and reported (stop by weight possible) */
  scale: boolean;
  /** water level sensor present and switched on (flush, descaling, rinse need it) */
  watertank: boolean;
  /** the machine's time zone (profile.tz) and its local day now */
  zone: string | undefined;
  today: number;
  /** bean slots of the machine (profile.limits.beans) */
  beanLimit: number | undefined;
  /** switch-on to ready in minutes, learned from cold starts or the default (core/schedule.ts) */
  lead: number;
  /** what the lead is based on: the API's measurement, null before it answered */
  heatup: HeatupInfo | null;
  /** may this user change this value or run this action now (protection S and owner commands only for the owner) */
  canEdit(id: string): boolean;
  /** an open command of this action locks its button (AD-12) */
  locked(action: string): boolean;
  tr(key: TextKey, vars?: TextVars): string;
  catalog: CommandCatalog;
}

/** The API's measurement of the machine's heat-up (GET /api/machines/{id}/heatup). */
export interface HeatupInfo {
  /** median of the last measured cold starts, null without any */
  seconds: number | null;
  samples: number;
}

export interface ScreenContextInput {
  view: MachineView;
  lang: Lang;
  now: number;
  /** values as shown: the stepper's pending value over the store's provisional overlay */
  value: Values;
  locked?: (action: string) => boolean;
  catalog?: CommandCatalog;
  heatup?: HeatupInfo | null;
}

interface Profile {
  sensors?: Record<string, boolean>;
  tz?: string;
  limits?: { beans?: number };
}

export function screenContext(input: ScreenContextInput): ScreenContext {
  const { view, lang, now, value } = input;
  const catalog = input.catalog ?? orioneCommandCatalog;
  const profile = (view.profile?.data ?? {}) as Profile;
  const sensors = profile.sensors ?? {};
  const controllable = controlsUsable(view, now);
  const isOwner = view.info?.role === 'owner';
  const scaleGroup = view.groups.scale?.data as { status?: string } | undefined;
  const zone = zoneOf(profile.tz);
  return {
    view,
    lang,
    now,
    value,
    num: (id, fallback = 0) => {
      const v = value(id);
      return typeof v === 'number' ? v : fallback;
    },
    controllable,
    isOwner,
    sensors,
    scale: sensors.scale === true && scaleGroup?.status !== undefined && scaleGroup.status !== 'off',
    watertank: sensors.watertank === true && value('hardware.sensors.watertank.enabled') === true,
    zone,
    today: localDayOf(now, zone),
    beanLimit: profile.limits?.beans,
    lead: leadMinutes(input.heatup?.seconds),
    heatup: input.heatup ?? null,
    canEdit: (id) => {
      if (!controllable) return false;
      const c = findCommand(catalog, id);
      if (!c || c.role === 'system') return false;
      return !(c.protection === 'S' || c.role === 'owner') || isOwner;
    },
    locked: input.locked ?? (() => false),
    tr: (key, vars) => t(lang, key, vars),
    catalog,
  };
}

/** A button that sends a command. */
export interface ActionView {
  label: string;
  enabled: boolean;
  /** its command is out and not answered yet: the button shows that it works (and stays locked, AD-12) */
  busy: boolean;
  input: CommandInput;
  /** ask before sending (catalog confirm, or a question of the screen such as "Entkalken abbrechen?") */
  ask: { title: string; text: string; ok: string; danger: boolean } | null;
}

export interface ActionOptions {
  label: TextKey;
  vars?: TextVars;
  /** further conditions of the screen (state of the machine) */
  when?: boolean;
  args?: Record<string, unknown>;
  expect?: Record<string, unknown>;
  /** the question before sending: its title (else the label), its text, the confirming button (else the label) */
  ask?: { title?: TextKey; text: TextKey; vars?: TextVars; ok?: TextKey; danger?: boolean };
}

/**
 * A machine action as a button: enabled only when the machine is controllable, the user may run it and no command of
 * it is open; confirm (the API requires it for catalog confirm actions) is set from the catalog, never by the screen.
 */
export function actionView(ctx: ScreenContext, action: string, o: ActionOptions): ActionView {
  const c = findCommand(ctx.catalog, action);
  const label = ctx.tr(o.label, o.vars);
  const input: CommandInput = {
    kind: 'action',
    action,
    ...(o.args ? { args: o.args } : {}),
    ...(o.expect ? { expect: o.expect } : {}),
    ...(c?.confirm ? { confirm: true } : {}),
  };
  const busy = ctx.locked(action);
  return {
    label,
    enabled: (o.when ?? true) && ctx.canEdit(action) && !busy,
    busy,
    input,
    ask: o.ask
      ? {
          title: o.ask.title ? ctx.tr(o.ask.title) : label,
          text: ctx.tr(o.ask.text, o.ask.vars),
          ok: o.ask.ok ? ctx.tr(o.ask.ok) : label,
          danger: o.ask.danger ?? false,
        }
      : null,
  };
}

/** Text of a key that comes as data (notes, causes): nested keys in the values are translated too. */
export function textOf(ctx: ScreenContext, key: string, vars: TextVars = {}): string {
  const out: TextVars = {};
  for (const [k, v] of Object.entries(vars)) out[k] = typeof v === 'string' && isKey(v) ? t(ctx.lang, v) : v;
  return isKey(key) ? t(ctx.lang, key, out) : key;
}

let keys: Set<string> | null = null;
function isKey(s: string): s is TextKey {
  keys ??= new Set<string>(textKeys());
  return keys.has(s);
}
